import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Check, X, CreditCard, Calendar, Users, HardDrive, 
  Briefcase, Crown, Sparkles, AlertTriangle, ArrowRight,
  Building2, Palette, Globe, Smartphone
} from 'lucide-react';
import api from '../../utils/api';

const BillingPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Handle success/cancel from Stripe
    if (searchParams.get('success') === 'true') {
      toast.success('Payment successful! Your plan has been updated.');
    } else if (searchParams.get('canceled') === 'true') {
      toast.info('Payment was canceled.');
    }
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, usageRes, historyRes, plansRes] = await Promise.all([
        api.get('/subscription'),
        api.get('/subscription/usage'),
        api.get('/billing/history'),
        api.get('/plans'),
      ]);
      setSubscription(subRes.data);
      setUsage(usageRes.data);
      setBillingHistory(historyRes.data);
      setPlans(plansRes.data.plans);
    } catch (error) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    setSelectedPlan(plan);
    setShowUpgradeDialog(true);
  };

  const handleUpgrade = async () => {
    if (!selectedPlan) return;
    setProcessingPayment(true);
    
    try {
      const response = await api.post(`/subscription/create-checkout?plan_type=${selectedPlan.type}`);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to create checkout session');
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription?')) return;
    
    try {
      await api.post('/subscription/cancel');
      toast.success('Subscription will be canceled at the end of the billing period');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'basic': return <Building2 size={24} />;
      case 'premium': return <Sparkles size={24} />;
      case 'white_label': return <Crown size={24} />;
      default: return <Building2 size={24} />;
    }
  };

  const getPlanColor = (planType) => {
    switch (planType) {
      case 'basic': return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'premium': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'white_label': return 'bg-purple-100 text-purple-800 border-purple-500';
      default: return 'bg-gray-100';
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">{t('common.loading', 'Loading...')}</div>;
  }

  const isTrialing = subscription?.status === 'trialing';
  const daysLeft = subscription?.days_left_in_trial;

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold mb-2">{t('billing.title', 'Billing & Plans')}</h1>
        <p className="text-gray-600">{t('billing.subtitle', 'Manage your subscription and billing')}</p>
      </div>

      {/* Trial Banner */}
      {isTrialing && daysLeft !== null && (
        <Card className={`border-2 ${daysLeft <= 3 ? 'border-orange-400 bg-orange-50' : 'border-purple-400 bg-purple-50'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${daysLeft <= 3 ? 'bg-orange-200' : 'bg-purple-200'}`}>
                  <Crown className={daysLeft <= 3 ? 'text-orange-600' : 'text-purple-600'} size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">
                    {daysLeft > 0 
                      ? `${daysLeft} ${t('billing.trial_days_left', 'days left in your White Label trial')}` 
                      : t('billing.trial_ends_today', 'Your trial ends today!')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t('billing.trial_access', 'You have full access to all White Label features. Choose a plan before your trial ends.')}
                  </p>
                </div>
              </div>
              <Button onClick={() => handleSelectPlan(plans.find(p => p.type === 'white_label'))}>
                Keep White Label
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Plan & Usage */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Current Plan */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {getPlanIcon(subscription?.plan_type)}
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`p-4 rounded-lg border-2 ${getPlanColor(subscription?.plan_type)}`}>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold">{subscription?.plan_details?.name}</h3>
                  <p className="text-sm opacity-75">
                    {subscription?.status === 'trialing' ? 'Free Trial' : 
                     subscription?.status === 'active' ? 'Active' : 
                     subscription?.status}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">€{subscription?.plan_details?.price_monthly}</p>
                  <p className="text-sm opacity-75">/month</p>
                </div>
              </div>
            </div>

            {subscription?.status === 'active' && (
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Next billing date:</span>
                  <span className="font-medium">
                    {subscription?.current_period_end ? 
                      new Date(subscription.current_period_end).toLocaleDateString() : 
                      'N/A'}
                  </span>
                </div>
                {subscription?.cancel_at_period_end && (
                  <p className="text-orange-600 text-sm">
                    <AlertTriangle size={14} className="inline mr-1" />
                    Subscription will be canceled at period end
                  </p>
                )}
              </div>
            )}

            <div className="mt-6 flex gap-3">
              {subscription?.status !== 'trialing' && !subscription?.cancel_at_period_end && (
                <Button variant="outline" onClick={handleCancelSubscription} className="flex-1">
                  Cancel Plan
                </Button>
              )}
              <Button 
                onClick={() => setShowUpgradeDialog(true)} 
                className="flex-1"
              >
                Change Plan
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Usage Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Current Usage</CardTitle>
            <CardDescription>This billing period</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Jobs */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2">
                  <Briefcase size={16} />
                  Jobs
                </span>
                <span>
                  {usage?.jobs_created} / {usage?.limits?.jobs_limit === -1 ? '∞' : usage?.limits?.jobs_limit}
                </span>
              </div>
              <Progress 
                value={usage?.limits?.jobs_limit === -1 ? 10 : (usage?.jobs_created / usage?.limits?.jobs_limit * 100)} 
                className="h-2"
              />
            </div>

            {/* Users */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2">
                  <Users size={16} />
                  Users
                </span>
                <span>{usage?.users_active} / {usage?.limits?.users_limit}</span>
              </div>
              <Progress 
                value={(usage?.users_active / usage?.limits?.users_limit * 100)} 
                className="h-2"
              />
            </div>

            {/* Storage */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2">
                  <HardDrive size={16} />
                  Storage
                </span>
                <span>{(usage?.storage_used_mb / 1024).toFixed(2)}GB / {usage?.limits?.storage_gb}GB</span>
              </div>
              <Progress 
                value={(usage?.storage_used_mb / (usage?.limits?.storage_gb * 1024) * 100)} 
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pricing Plans */}
      <Card>
        <CardHeader>
          <CardTitle>Available Plans</CardTitle>
          <CardDescription>Choose the plan that's right for your business</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div 
                key={plan.type}
                className={`border-2 rounded-xl p-6 relative ${
                  plan.type === 'white_label' ? 'border-purple-500 bg-purple-50/50' :
                  plan.type === 'premium' ? 'border-blue-300' : 'border-gray-200'
                } ${subscription?.plan_type === plan.type ? 'ring-2 ring-primary' : ''}`}
              >
                {plan.type === 'white_label' && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600">
                    MOST POPULAR
                  </Badge>
                )}

                <div className="text-center mb-6">
                  <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-3 ${
                    plan.type === 'white_label' ? 'bg-purple-200 text-purple-700' :
                    plan.type === 'premium' ? 'bg-blue-200 text-blue-700' : 'bg-gray-200 text-gray-700'
                  }`}>
                    {getPlanIcon(plan.type)}
                  </div>
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  <div className="mt-2">
                    <span className="text-4xl font-bold">€{plan.price_monthly}</span>
                    <span className="text-gray-500">/mo</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <Check size={16} className="text-green-600" />
                    {plan.jobs_limit === -1 ? 'Unlimited jobs' : `${plan.jobs_limit} jobs/month`}
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check size={16} className="text-green-600" />
                    Up to {plan.users_limit} users
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check size={16} className="text-green-600" />
                    {plan.storage_gb}GB storage
                  </li>
                  
                  {plan.features.includes('advanced_reporting') && (
                    <li className="flex items-center gap-2 text-sm">
                      <Check size={16} className="text-green-600" />
                      Advanced reporting
                    </li>
                  )}
                  {plan.features.includes('api_access') && (
                    <li className="flex items-center gap-2 text-sm">
                      <Check size={16} className="text-green-600" />
                      API access
                    </li>
                  )}
                  {plan.features.includes('qr_batch_generator') && (
                    <li className="flex items-center gap-2 text-sm">
                      <Check size={16} className="text-green-600" />
                      QR Batch Generator
                    </li>
                  )}
                  {plan.features.includes('custom_branding') && (
                    <li className="flex items-center gap-2 text-sm">
                      <Palette size={16} className="text-purple-600" />
                      Custom branding
                    </li>
                  )}
                  {plan.features.includes('custom_domain') && (
                    <li className="flex items-center gap-2 text-sm">
                      <Globe size={16} className="text-purple-600" />
                      Custom domain
                    </li>
                  )}
                  {plan.features.includes('white_label_app') && (
                    <li className="flex items-center gap-2 text-sm">
                      <Smartphone size={16} className="text-purple-600" />
                      White-label app
                    </li>
                  )}
                  {plan.features.includes('ai_features') && (
                    <li className="flex items-center gap-2 text-sm">
                      <Sparkles size={16} className="text-purple-600" />
                      AI features included
                    </li>
                  )}
                  
                  {!plan.features.includes('advanced_reporting') && (
                    <li className="flex items-center gap-2 text-sm text-gray-400">
                      <X size={16} />
                      Advanced reporting
                    </li>
                  )}
                  {!plan.features.includes('custom_branding') && (
                    <li className="flex items-center gap-2 text-sm text-gray-400">
                      <X size={16} />
                      Custom branding
                    </li>
                  )}
                </ul>

                <Button 
                  className="w-full"
                  variant={subscription?.plan_type === plan.type ? 'outline' : 'default'}
                  onClick={() => handleSelectPlan(plan)}
                  disabled={subscription?.plan_type === plan.type}
                >
                  {subscription?.plan_type === plan.type ? 'Current Plan' : 'Select Plan'}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Billing History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard size={20} />
            Billing History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {billingHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CreditCard size={48} className="mx-auto mb-4 opacity-50" />
              <p>No billing history yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {billingHistory.map((item) => (
                <div key={item.id} className="flex justify-between items-center p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">{item.description}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(item.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">€{item.amount.toFixed(2)}</p>
                    <Badge variant={item.status === 'paid' ? 'default' : 'secondary'}>
                      {item.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedPlan?.type === subscription?.plan_type ? 'Current Plan' : 
               selectedPlan?.price_monthly > (subscription?.plan_details?.price_monthly || 0) ? 'Upgrade Plan' : 'Change Plan'}
            </DialogTitle>
            <DialogDescription>
              {isTrialing ? 'Your trial will continue until the end date.' : 
               'Changes will be prorated on your next bill.'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPlan && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-lg">{selectedPlan.name}</h3>
                    <p className="text-sm text-gray-500">
                      {selectedPlan.jobs_limit === -1 ? 'Unlimited' : selectedPlan.jobs_limit} jobs • 
                      {selectedPlan.users_limit} users • 
                      {selectedPlan.storage_gb}GB
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">€{selectedPlan.price_monthly}</p>
                    <p className="text-sm text-gray-500">/month</p>
                  </div>
                </div>
              </div>

              {selectedPlan.type !== subscription?.plan_type && (
                <Button 
                  onClick={handleUpgrade} 
                  className="w-full"
                  disabled={processingPayment}
                >
                  {processingPayment ? 'Processing...' : (
                    <>
                      Continue to Checkout
                      <ArrowRight size={18} className="ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingPage;
